import {
    FLEXI_24_PRODUCT_TYPE,
    FLEXI_LOAN_PRODUCT_TYPE,
    getMonthsfromDays
} from '../multiLoanDashboard/accountsSelector';
import {
    FormalOffers,
    NormalisedRepaymentSchedule,
    DrawdownRepaymentSchedule,
    FormalOffer
} from './MyOffers.types';

export const getFlexiLoanFormalOffers = (
    formal_offers: FormalOffers
): FormalOffers => {
    const formalOffers = formal_offers.filter(
        (offer) =>
            offer.product_type === FLEXI_LOAN_PRODUCT_TYPE ||
            offer.product_type === FLEXI_24_PRODUCT_TYPE
    );
    return formalOffers;
};
export const isOfferTimedOut = (formal_offers: FormalOffers): boolean => {
    const formalOffer = formal_offers[0];
    return formalOffer.is_timed_out!;
};

export const getAvailableCash = (formal_offers: FormalOffers): number => {
    //Currently we assume all offer amounts are the same
    const formalOffer = formal_offers[0];
    return Math.floor(formalOffer.available_cash! * 100) / 100;
};

export const getMinAvailableCash = (formal_offers: FormalOffers) => {
    //Available cash amount can vary for offers - currently only in DE
    const availableCash = formal_offers.map(
        (offer) => offer.available_cash || 0
    );
    return Math.floor(Math.min(...availableCash));
};

export const getMaxAvailableCash = (formal_offers: FormalOffers) => {
    return Math.floor(
        formal_offers.reduce<number>((max, offer) => {
            const maxFromOffer = offer.available_cash || 0;
            return maxFromOffer > max ? maxFromOffer : max;
        }, 0) || 150
    );
};

export const getTimeOutDate = (formalOffers: FormalOffers): string => {
    //Currently we assume all timeout dates are the same
    const formalOffer = formalOffers[0];

    const formalOfferTimeOut = formalOffer.timeout!;

    return new Date(formalOfferTimeOut).toISOString();
};

export const getOfferById = (
    formalOffers: FormalOffers,
    offerId: string
): FormalOffer => {
    const offer = formalOffers.find((offer) => offer.offer_id === offerId);
    return offer!;
};

export const normaliseRepaymentSchedule = ({
    formalOffer,
    repaymentSchedule
}: {
    formalOffer: FormalOffer;
    repaymentSchedule: DrawdownRepaymentSchedule;
}): NormalisedRepaymentSchedule => {
    const { duration, offer_id, withdrawal_fee_percentage, interest_rates } =
        formalOffer;
    const { summary, schedule } = repaymentSchedule;
    return {
        offerId: offer_id!,
        duration: getLoanDurationInMonths(duration!),
        monthlyRepayments:
            (summary.is_equal_repayment
                ? summary.simplified_schedule?.equal_repayments?.amount
                : schedule[3]?.total) || 0,
        interestRate: interest_rates![0].rate!,
        repayments: schedule,
        totalPrincipal: summary.total_credit_amount,
        totalRepayment: summary.total_payable,
        totalInterest: summary.total_interest,
        totalFees: summary.total_fees,
        drawdownFee: withdrawal_fee_percentage!,
        hasFees: summary.total_fees! > 0
    };
};

export const normaliseEarlyRepaymentSchedule = (
    repaymentSchedule: DrawdownRepaymentSchedule,
    offerId: string
): NormalisedRepaymentSchedule => {
    const { summary, schedule } = repaymentSchedule;
    return {
        offerId: offerId,
        monthlyRepayments: 0,
        interestRate: 0,
        totalFees: schedule[0].total_fees,
        totalRepayment: summary.total_payable,
        totalInterest: summary.total_interest,
        repayments: schedule,
        amountSaved: summary.amount_saved,
        drawdownFee: 0,
        hasFees: summary.total_fees! > 0,
        totalPrincipal: summary.total_credit_amount
    };
};

export const getProductTypeOfSelectedOffer = (
    offerId: string,
    formalOffers: FormalOffers
): string => {
    const offer = formalOffers.find((offer) => offer.offer_id === offerId);
    return offer!.product_type!;
};

export const getScheduleForModal = (
    offerId: string,
    drawdownSchedules: NormalisedRepaymentSchedule[]
): NormalisedRepaymentSchedule => {
    const schedule = drawdownSchedules.find(
        (schedule) => schedule.offerId === offerId
    );
    return schedule!;
};

export const getLoanDurationInMonths = (duration: {
    amount: number;
    unit: 'days' | 'months' | 'years';
}): number => {
    const { amount, unit } = duration!;
    if (unit === 'days') {
        return getMonthsfromDays(amount);
    }
    if (unit === 'years') {
        return amount * 12;
    }
    return amount;
};

export const getNonInterestOnlyPaymentAmount = (
    drawdownSchedule: DrawdownRepaymentSchedule['schedule']
): number => {
    const scheduleItem = drawdownSchedule.find(
        (schedule: DrawdownRepaymentSchedule['schedule'][0]) =>
            //interest-only schedule items have a principal of 0
            schedule.principal > 0
    );
    return scheduleItem?.total || 0;
};

export const getInterestOnlySchedule = (
    drawdownSchedule: DrawdownRepaymentSchedule['schedule'] | null
): DrawdownRepaymentSchedule['schedule'] | null => {
    if (!drawdownSchedule) return null;
    const interestOnlySchedules = drawdownSchedule.filter(
        //interest-only schedule items have a principal of 0
        (schedule) => schedule.principal === 0
    );
    return interestOnlySchedules;
};

export const isInterestOnly = (formal_offer: FormalOffer): boolean => {
    return Boolean(
        formal_offer.interest_only_repayments &&
            formal_offer.interest_only_repayments > 0
    );
};
export const isRBLOffer = (formal_offer: FormalOffer): boolean => {
    return formal_offer.product_type === 'merchant_cash_advance';
};
